import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Coffee, 
  Edit,
  ToggleLeft,
  ToggleRight,
  Clock,
  ChefHat,
  Star,
  Trash2,
  ChevronDown,
  Settings,
  Download,
  Upload,
  FileText,
  MoreVertical
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { dataService, MenuItem } from '../../services/dataService';
import { useCategories } from '../../hooks/useCategories';
import { CSVUtils } from '../../utils/csvUtils';
import AddMenuItemModal from './AddMenuItemModal';
import EditMenuItemModal from './EditMenuItemModal';
import MenuItemAddOnsConfig from './MenuItemAddOnsConfig';
import CSVImportModal from './CSVImportModal';
import MenuImageImportModal from './MenuImageImportModal';

// Custom Animated Select Component
const AnimatedSelect: React.FC<{
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
  className?: string;
}> = ({ value, onChange, options, placeholder, className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const selectRef = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    if (isOpen) {
      setIsClosing(true);
      setTimeout(() => {
        setIsOpen(false);
        setIsClosing(false);
      }, 300);
    } else {
      if (selectRef.current) {
        const rect = selectRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width
        });
      }
      setIsOpen(true);
    }
  };

  const handleSelect = (option: string) => {
    onChange(option);
    handleToggle();
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
      if (isOpen) {
        handleToggle();
      }
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const selectedOption = options.find(option => option === value) || placeholder || 'Select...';

  return (
    <>
      <div ref={selectRef} className={`custom-select ${className}`}>
        <div 
          className="custom-select-trigger animate-input-focus"
          onClick={handleToggle}
        >
          <span className="truncate">{selectedOption}</span>
          <ChevronDown 
            className={`w-4 h-4 text-gray-400 dark:text-gray-500 transition-transform duration-300 ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </div>
      </div>
      {isOpen && createPortal(
        <div 
          className={`custom-select-dropdown-portal ${isClosing ? 'closing' : 'open'}`}
          style={{
            position: 'absolute',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
            zIndex: 99999
          }}
        >
          {options.map((option) => (
            <div
              key={option}
              className={`custom-select-option ${
                option === value ? 'selected' : ''
              }`}
              onClick={() => handleSelect(option)}
            >
              {option === 'all' ? 'All Categories' : 
               option === 'available' ? 'Available' : 
               option === 'unavailable' ? 'Unavailable' : option}
            </div>
          ))}
        </div>,
        document.body
      )}
    </>
  );
};

const MenuItems: React.FC = () => {
  const navigate = useNavigate();
  const { tenantData } = useAuth();
  const { getCategoryNamesWithAll, loadCategories } = useCategories('menuItem');
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [availabilityFilter, setAvailabilityFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showPhotoImportModal, setShowPhotoImportModal] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const actionsMenuRef = useRef<HTMLDivElement>(null);
  const [photoImportSummary, setPhotoImportSummary] = useState<{
    created: number;
    updated: number;
    totalRows: number;
    warnings: string[];
  } | null>(null);
  const [selectedMenuItem, setSelectedMenuItem] = useState<MenuItem | null>(null);
  const [showAddOnsConfig, setShowAddOnsConfig] = useState(false);
  const [configuringMenuItem, setConfiguringMenuItem] = useState<MenuItem | null>(null);

  const categories = getCategoryNamesWithAll();
  const availabilityOptions = ['all', 'available', 'unavailable'];

  const loadMenuItems = useCallback(async () => {
    try {
      setLoading(true);
      const items = await dataService.getMenuItems(tenantData!.id);
      setMenuItems(items);
    } catch (error) {
      console.error('Error loading menu items:', error);
    } finally {
      setLoading(false);
    }
  }, [tenantData]);

  useEffect(() => {
    if (tenantData?.id) {
      loadMenuItems();
    }
  }, [tenantData, loadMenuItems]);

  const handleToggleAvailability = async (item: MenuItem) => {
    try {
      await dataService.updateMenuItemAvailability(tenantData!.id, item.id!, !item.isAvailable);
      await loadMenuItems();
    } catch (error) {
      console.error('Error updating menu item availability:', error);
    }
  };

  const handleToggleTodaysSpecial = async (item: MenuItem) => {
    try {
      const updateData = { isTodaysSpecial: !item.isTodaysSpecial };
      // Filter out undefined values
      const cleanUpdateData = Object.entries(updateData).reduce((acc, [key, value]) => {
        if (value !== undefined) {
          (acc as any)[key] = value;
        }
        return acc;
      }, {} as Partial<MenuItem>);
      
      await dataService.update<MenuItem>(tenantData!.id, 'menuItems', item.id!, cleanUpdateData);
      await loadMenuItems();
    } catch (error) {
      console.error('Error updating today\'s special status:', error);
    }
  };

  const filteredItems = menuItems.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
    const matchesAvailability = availabilityFilter === 'all' || 
                               (availabilityFilter === 'available' && item.isAvailable) ||
                               (availabilityFilter === 'unavailable' && !item.isAvailable);
    return matchesSearch && matchesCategory && matchesAvailability;
  });

  const handleAddMenuItem = async () => {
    await loadMenuItems();
    setShowAddModal(false);
  };

  const handleEdit = (item: MenuItem) => {
    setSelectedMenuItem(item);
    setShowEditModal(true);
  };

  const handleEditSuccess = async () => {
    await loadMenuItems();
    setShowEditModal(false);
    setSelectedMenuItem(null);
  };

  const handleConfigureAddOns = (item: MenuItem) => {
    setConfiguringMenuItem(item);
    setShowAddOnsConfig(true);
  };

  const handleAddOnsConfigSuccess = async () => {
    await loadMenuItems();
    setShowAddOnsConfig(false);
    setConfiguringMenuItem(null);
  };

  const handleDelete = async (item: MenuItem) => {
    const confirmMessage = `Are you sure you want to delete "${item.name}"?\n\nThis action cannot be undone and will permanently remove the menu item from your system.`;
    
    if (window.confirm(confirmMessage)) {
      try {
        await dataService.delete(tenantData!.id, 'menuItems', item.id!);
        await loadMenuItems();
      } catch (error) {
        console.error('Error deleting menu item:', error);
        alert('Failed to delete menu item. Please try again.');
      }
    }
  };

  const handleDownloadTemplate = () => {
    const template = CSVUtils.generateTemplate('menuItem');
    CSVUtils.downloadCSV(template, 'menu-items-template.csv');
  };

  const handleExportCSV = (exportAll: boolean = false) => {
    const dataToExport = exportAll ? menuItems : filteredItems;
    const csvContent = CSVUtils.exportToCSV(dataToExport, 'menuItem');
    const filename = exportAll ? 'menu-items-all.csv' : 'menu-items-filtered.csv';
    CSVUtils.downloadCSV(csvContent, filename);
  };

  const handleImportSuccess = async () => {
    await loadMenuItems();
    setShowImportModal(false);
  };

  const handlePhotoImportSuccess = async (summary: {
    created: number;
    updated: number;
    totalRows: number;
    warnings: string[];
  }) => {
    await Promise.all([loadMenuItems(), loadCategories()]);
    setPhotoImportSummary(summary);
    setShowPhotoImportModal(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(event.target as Node)) {
        setShowActionsMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-page-enter">
      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 sm:justify-between sm:items-center animate-fade-in-up relative z-30">
        <div className="flex gap-2 w-full sm:w-auto items-center">
          {/* AI Feature - flex-1 on mobile, sm:w-auto on desktop */}
          <div className="relative flex-1 sm:flex-initial rounded-lg p-[2px] overflow-hidden isolate animate-button-hover">
            <div className="pointer-events-none absolute inset-0 rounded-lg">
              <div className="h-full w-full bg-[conic-gradient(from_0deg,#8b5cf6,#06b6d4,#22c55e,#f59e0b,#ef4444,#8b5cf6)] animate-[spin_3.5s_linear_infinite] scale-[1.8] origin-center" />
            </div>
            <button
              onClick={() => setShowPhotoImportModal(true)}
              className="btn-secondary w-full sm:w-auto flex items-center justify-center relative z-10 bg-white dark:bg-gray-800"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7h4l2-2h6l2 2h4v12H3V7zm9 2a4 4 0 100 8 4 4 0 000-8z" />
              </svg>
              Import From Photo (AI)
              <span className="ml-2 inline-flex items-center rounded-full bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                New
              </span>
            </button>
          </div>

          {/* Overflow menu for CSV actions - keeps compact size on mobile */}
          <div className="relative" ref={actionsMenuRef}>
            <button
              onClick={() => setShowActionsMenu(prev => !prev)}
              className="btn-secondary px-3 py-2.5 rounded-lg flex items-center justify-center animate-button-hover"
              title="More actions"
            >
              <MoreVertical className="w-4 h-4" />
            </button>

            {showActionsMenu && (
              <div className="absolute right-0 top-full mt-1 w-60 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-[100] overflow-hidden">
                <button
                  onClick={() => {
                    handleDownloadTemplate();
                    setShowActionsMenu(false);
                  }}
                  className="w-full px-4 py-2.5 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Download Template
                </button>
                <button
                  onClick={() => {
                    handleExportCSV(false);
                    setShowActionsMenu(false);
                  }}
                  className="w-full px-4 py-2.5 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export Filtered ({filteredItems.length})
                </button>
                <button
                  onClick={() => {
                    handleExportCSV(true);
                    setShowActionsMenu(false);
                  }}
                  className="w-full px-4 py-2.5 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export All ({menuItems.length})
                </button>
                <button
                  onClick={() => {
                    setShowImportModal(true);
                    setShowActionsMenu(false);
                  }}
                  className="w-full px-4 py-2.5 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Import CSV
                </button>
              </div>
            )}
          </div>
        </div>
        
        {/* Add Menu Item Button */}
        <button 
          onClick={() => setShowAddModal(true)}
          className="btn-primary w-full sm:w-auto flex items-center justify-center animate-button-hover"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Menu Item
        </button>
      </div>

      {photoImportSummary && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 animate-fade-in-up">
          <div className="text-sm text-green-800 dark:text-green-300">
            Menu photo import complete: created {photoImportSummary.created}, updated {photoImportSummary.updated}, processed {photoImportSummary.totalRows} rows.
            {photoImportSummary.warnings.length > 0 && (
              <span className="ml-1">Warnings: {photoImportSummary.warnings.length}.</span>
            )}
          </div>
          <button
            onClick={() => navigate('/features')}
            className="btn-secondary text-sm"
          >
            Download QR In Features
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="card p-4 sm:p-6 animate-fade-in-up animation-delay-300">
        <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <div className="search-input-icon">
                <Search />
              </div>
              <input
                type="text"
                placeholder="Search menu items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10 sm:pl-10 animate-input-focus"
              />
            </div>
          </div>

          {/* Category & Availability Filters Container */}
          <div className="grid grid-cols-2 gap-2 sm:flex sm:gap-4 w-full sm:w-auto">
            {/* Category Filter */}
            <div className="w-full sm:w-48">
              <AnimatedSelect
                value={categoryFilter}
                onChange={setCategoryFilter}
                options={categories}
                placeholder="All Categories"
                className="category-filter"
              />
            </div>

            {/* Availability Filter */}
            <div className="w-full sm:w-48">
              <AnimatedSelect
                value={availabilityFilter}
                onChange={setAvailabilityFilter}
                options={availabilityOptions}
                placeholder="All Items"
                className="availability-filter"
              />
            </div>
          </div>
        </div>
      </div>

            {/* Menu Items Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
        {filteredItems.map((item, index) => (
          <div 
            key={item.id} 
            className={`bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden animate-card-enter animate-card-hover ${
              index < 5 ? `animate-stagger-${index + 1}` : 'animate-stagger-5'
            }`}
          >
              {/* Header */}
              <div className="p-3 sm:p-4 pb-2 border-b border-gray-100 dark:border-gray-700">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate mb-1">
                    {item.name}
                  </h3>
                  <div className="flex items-center space-x-1 flex-wrap gap-1">
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                      {item.category}
                    </span>
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium border ${
                      item.isAvailable 
                        ? 'bg-green-50 text-green-700 border-green-200' 
                        : 'bg-red-50 text-red-700 border-red-200'
                    }`}>
                      {item.isAvailable ? 'Available' : 'Unavailable'}
                    </span>
                    
                    {/* Special Status Badges */}
                    {item.isTodaysSpecial && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
                        <Star className="w-3 h-3 mr-1" />
                        Today's Special
                      </span>
                    )}
                    {item.isFeatured && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
                        <Star className="w-3 h-3 mr-1" />
                        Featured
                      </span>
                    )}
                    {item.isSpecialOffer && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                        Special Offer
                      </span>
                    )}
                    {item.supportsAddOns && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
                        <Settings className="w-3 h-3 mr-1" />
                        Add-Ons
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-3 sm:p-4 space-y-2">
              {/* Price - Prominent */}
              <div className="bg-primary-50 rounded p-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-primary-700">Price</span>
                  <div className="text-lg font-bold text-primary-900">
                    ₹{item.price.toFixed(2)}
                  </div>
                </div>
              </div>

              {/* Key Details */}
              <div className="grid grid-cols-2 gap-2">
                <div className="text-center">
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Prep</div>
                  <div className="text-sm font-semibold text-gray-900 dark:text-white flex items-center justify-center">
                    <Clock className="w-3 h-3 mr-1" />
                    {item.preparationTime}m
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Items</div>
                  <div className="text-sm font-semibold text-gray-900 dark:text-white flex items-center justify-center">
                    <ChefHat className="w-3 h-3 mr-1" />
                    {item.ingredients?.length || 0}
                  </div>
                </div>
              </div>

              {/* Description */}
              {item.description && (
                <div className="pt-2 border-t border-gray-100">
                  <p className="text-xs text-gray-600 line-clamp-2">
                    {item.description}
                  </p>
                </div>
              )}

              {/* Ingredients Preview */}
              {item.ingredients && item.ingredients.length > 0 && (
                <div className="pt-2 border-t border-gray-100">
                  <div className="text-xs font-medium text-gray-500 mb-1">Ingredients</div>
                  <div className="flex flex-wrap gap-1">
                    {item.ingredients.slice(0, 2).map((ingredient, index) => (
                      <span key={index} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-600">
                        {ingredient.quantity} {ingredient.unit}
                      </span>
                    ))}
                    {item.ingredients.length > 2 && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-600">
                        +{item.ingredients.length - 2}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Auto Inventory Deduction */}
              {item.autoDeductInventory && (
                <div className="bg-blue-50 rounded p-1.5 text-xs text-blue-700">
                  <span className="font-medium">Auto-deduction enabled</span>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="p-3 sm:p-4 pt-0">
              <div className="flex space-x-1">
                <button
                  onClick={() => handleToggleAvailability(item)}
                  className={`flex-1 px-2 py-1.5 rounded text-xs font-medium transition-colors duration-200 flex items-center justify-center space-x-1 ${
                    item.isAvailable
                      ? 'bg-red-600 hover:bg-red-700 text-white'
                      : 'bg-green-600 hover:bg-green-700 text-white'
                  }`}
                >
                  {item.isAvailable ? (
                    <>
                      <ToggleLeft className="w-3 h-3" />
                      <span>Disable</span>
                    </>
                  ) : (
                    <>
                      <ToggleRight className="w-3 h-3" />
                      <span>Enable</span>
                    </>
                  )}
                </button>
                
                <button
                  onClick={() => handleToggleTodaysSpecial(item)}
                  className={`px-2 py-1.5 rounded text-xs font-medium transition-colors duration-200 flex items-center justify-center space-x-1 ${
                    item.isTodaysSpecial
                      ? 'bg-yellow-500 hover:bg-yellow-600 text-white'
                      : 'bg-gray-100 hover:bg-yellow-100 text-gray-700 hover:text-yellow-700'
                  }`}
                  title={item.isTodaysSpecial ? "Remove from Today's Special" : "Mark as Today's Special"}
                >
                  <Star className={`w-3 h-3 ${item.isTodaysSpecial ? 'fill-current' : ''}`} />
                </button>
                
                <button 
                  onClick={() => handleConfigureAddOns(item)}
                  className="bg-blue-100 hover:bg-blue-200 text-blue-700 px-2 py-1.5 rounded text-xs font-medium transition-colors duration-200 flex items-center justify-center"
                  title="Configure Add-Ons"
                >
                  <Settings className="w-3 h-3" />
                </button>
                
                <button 
                  onClick={() => handleEdit(item)}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-2 py-1.5 rounded text-xs font-medium transition-colors duration-200 flex items-center justify-center"
                  title="Edit Item"
                >
                  <Edit className="w-3 h-3" />
                </button>
                
                <button 
                  onClick={() => handleDelete(item)}
                  className="bg-red-100 hover:bg-red-200 text-red-700 px-2 py-1.5 rounded text-xs font-medium transition-colors duration-200 flex items-center justify-center"
                  title="Delete Item"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredItems.length === 0 && (
        <div className="col-span-full">
                      <div className="text-center py-12 bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
            <Coffee className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No menu items found</h3>
            <p className="text-sm text-gray-600 mb-4 max-w-md mx-auto">
              {searchTerm || categoryFilter !== 'all' || availabilityFilter !== 'all'
                ? 'No menu items match your current search or filter criteria.' 
                : 'Get started by adding your first menu item to build your cafe menu.'
              }
            </p>
            <button 
              onClick={() => setShowAddModal(true)}
              className="btn-primary text-sm px-4 py-2"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add First Item
            </button>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <AddMenuItemModal
          onClose={() => setShowAddModal(false)}
          onSuccess={handleAddMenuItem}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && selectedMenuItem && (
        <EditMenuItemModal
          menuItem={selectedMenuItem}
          onClose={() => setShowEditModal(false)}
          onSuccess={handleEditSuccess}
        />
      )}

      {/* Add-Ons Configuration Modal */}
      {showAddOnsConfig && configuringMenuItem && (
        <MenuItemAddOnsConfig
          menuItem={configuringMenuItem}
          onClose={() => setShowAddOnsConfig(false)}
          onSuccess={handleAddOnsConfigSuccess}
        />
      )}

      {showImportModal && (
        <CSVImportModal
          type="menuItem"
          onClose={() => setShowImportModal(false)}
          onSuccess={handleImportSuccess}
        />
      )}

      {showPhotoImportModal && (
        <MenuImageImportModal
          onClose={() => setShowPhotoImportModal(false)}
          onSuccess={handlePhotoImportSuccess}
        />
      )}
    </div>
  );
};

export default MenuItems; 